import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import type { Task, TaskAssignee, TaskPriority, TaskStatus } from '../types/task';

// Add this interface for AI reasoning
interface AIPriorityReason {
  taskId: string;
  oldPriority: TaskPriority;
  newPriority: TaskPriority;
  reason: string;
}

// Add interface for the prioritization status
interface PrioritizationStatus {
  lastPrioritizationDate: string | null;
  isConfirmed: boolean;
}

// Add interface for blocker detection
interface BlockerDetectionStatus {
  isOpen: boolean;
  lastDetectionDate: string | null;
}

const priorityColors = {
  urgent: 'text-red-500 bg-red-500/20',
  high: 'text-orange-500 bg-orange-500/20',
  medium: 'text-yellow-500 bg-yellow-500/20',
  low: 'text-green-500 bg-green-500/20',
  none: 'text-gray-400 bg-gray-400/20'
};

const statusColors = {
  'to do': 'text-gray-400 bg-gray-400/20',
  'in progress': 'text-blue-500 bg-blue-500/20',
  'done': 'text-green-500 bg-green-500/20',
  'blocked': 'text-red-500 bg-red-500/20',
  'review': 'text-purple-500 bg-purple-500/20'
};

// Map priorities to numeric values for sorting
const priorityOrder: Record<TaskPriority, number> = {
  'urgent': 1,
  'high': 2,
  'medium': 3,
  'low': 4,
  'none': 5
};

// Sort tasks by due date and then by priority
const sortTasks = (tasks: Task[]): Task[] => {
  return [...tasks].sort((a, b) => {
    // First compare due dates
    const dateA = new Date(a.dueDate);
    const dateB = new Date(b.dueDate);

    const dateDiff = dateA.getTime() - dateB.getTime();

    // If dates are the same, compare by priority
    if (dateDiff === 0) {
      return priorityOrder[a.priority] - priorityOrder[b.priority];
    }

    return dateDiff;
  });
};

const Tasks = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedTask, setEditedTask] = useState<Task | null>(null);
  const [isAIProcessing, setIsAIProcessing] = useState(false);
  const [showAIReasons, setShowAIReasons] = useState(false);
  const [aiReasons, setAIReasons] = useState<AIPriorityReason[]>([]);
  const [pendingTasks, setPendingTasks] = useState<Task[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  const [validationPrompt, setValidationPrompt] = useState('');
  const [prioritizationStatus, setPrioritizationStatus] = useState<PrioritizationStatus>({
    lastPrioritizationDate: null,
    isConfirmed: true
  });
  const [aiPriorities, setAIPriorities] = useState<Record<string, TaskPriority>>({});
  // Add blocker detection state
  const [blockerDetection, setBlockerDetection] = useState<BlockerDetectionStatus>({
    isOpen: false,
    lastDetectionDate: null
  });
  // Add this to the state declarations at the beginning of the component
  const [selectedReason, setSelectedReason] = useState<AIPriorityReason | null>(null);

  useEffect(() => {
    // Simulate loading data from backend
    const timer = setTimeout(() => {
      setTasks(dummyTasks);
      setLoading(false);

      // Check if we need to run automatic prioritization
      const today = new Date().toISOString().split('T')[0];
      const lastPrioritizationDate = sessionStorage.getItem('lastPrioritizationDate');

      if (lastPrioritizationDate !== today) {
        // First load of the day - setup different AI priorities
        const initialAiPriorities: Record<string, TaskPriority> = {};
        const priorities: TaskPriority[] = ['urgent', 'high', 'medium', 'low', 'none'];

        // Generate initial AI priorities different from current ones
        dummyTasks.forEach(task => {
          const currentIndex = priorities.indexOf(task.priority);
          // Choose a different priority (shift by 1 or 2 positions)
          const shift = 1 + Math.floor(Math.random() * 2);
          let newIndex = (currentIndex + shift) % priorities.length;
          initialAiPriorities[task.id] = priorities[newIndex];
        });

        setAIPriorities(initialAiPriorities);

        // Generate reasons for priority changes
        const initialReasons: AIPriorityReason[] = [];
        dummyTasks.forEach(task => {
          if (initialAiPriorities[task.id] !== task.priority) {
            initialReasons.push({
              taskId: task.id,
              oldPriority: task.priority,
              newPriority: initialAiPriorities[task.id],
              reason: getReason(task, initialAiPriorities[task.id], Math.floor((new Date(task.dueDate).getTime() - Date.now()) / (1000 * 3600 * 24)))
            });
          }
        });

        setAIReasons(initialReasons);
        sessionStorage.setItem('lastPrioritizationDate', today);
        setPrioritizationStatus({
          lastPrioritizationDate: today,
          isConfirmed: false
        });
        setShowAIReasons(true);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const handleTaskClick = (task: Task) => {
    setSelectedTask(task);
    setEditedTask(task);
  };

  const handleClosePopup = () => {
    setSelectedTask(null);
    setIsEditing(false);
  };

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, field: keyof Task) => {
    if (!editedTask) return;

    setEditedTask({
      ...editedTask,
      [field]: e.target.value
    });
  };

  const handleStatusChange = (status: TaskStatus) => {
    if (!editedTask) return;

    setEditedTask({
      ...editedTask,
      status
    });
  };

  const handlePriorityChange = (priority: TaskPriority) => {
    if (!editedTask) return;

    setEditedTask({
      ...editedTask,
      priority
    });
  };

  const handleTaskCompletion = (e: React.MouseEvent, taskId: string) => {
    e.stopPropagation(); // Prevent opening the task popup

    // Find the task
    const taskToUpdate = tasks.find(t => t.id === taskId);
    if (!taskToUpdate) return;

    // Toggle between 'done' and previous status (or 'to do' if it was 'done')
    const newStatus: TaskStatus = taskToUpdate.status === 'done'
      ? 'to do'
      : 'done';

    // Update the task in the state
    setTasks(tasks.map(task =>
      task.id === taskId
        ? { ...task, status: newStatus }
        : task
    ));
  };

  const handleAssigneeChange = (assigneeId: string, checked: boolean) => {
    if (!editedTask) return;

    if (checked) {
      // Add assignee from available team members
      const newAssignee = dummyTeamMembers.find(m => m.id === assigneeId);
      if (newAssignee) {
        setEditedTask({
          ...editedTask,
          assignees: [...editedTask.assignees, {
            id: newAssignee.id,
            name: newAssignee.name,
            inChatbotGG: true,
            avatar: newAssignee.photo
          }]
        });
      }
    } else {
      // Remove assignee
      setEditedTask({
        ...editedTask,
        assignees: editedTask.assignees.filter(a => a.id !== assigneeId)
      });
    }
  };

  const handleTagsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!editedTask) return;

    setEditedTask({
      ...editedTask,
      tags: e.target.value.split(',').map(tag => tag.trim())
    });
  };

  const handleSaveChanges = () => {
    if (!editedTask) return;

    // Check if this is a new task or an existing one
    const isNewTask = !tasks.some(t => t.id === editedTask.id);

    if (isNewTask) {
      setTasks([...tasks, editedTask]);
    } else {
      setTasks(tasks.map(task =>
        task.id === editedTask.id ? editedTask : task
      ));
    }

    setSelectedTask(editedTask);
    setIsEditing(false);
  };

  const handleAddNewTask = () => {
    const newTask: Task = {
      id: `task-${Date.now()}`,
      name: '',
      description: '',
      dueDate: new Date().toISOString(),
      assignees: [],
      priority: 'medium',
      status: 'to do',
      tags: [],
      subtasks: []
    };

    setEditedTask(newTask);
    setSelectedTask(newTask);
    setIsEditing(true);
  };

  const handleDeleteTask = () => {
    if (!selectedTask) return;

    setTasks(tasks.filter(task => task.id !== selectedTask.id));
    setSelectedTask(null);
    setIsEditing(false);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleAIReprioritize = () => {
    // Dummy implementation - previously this was random, now we'll add reasoning
    const priorities: TaskPriority[] = ['urgent', 'high', 'medium', 'low', 'none'];
    const reasons = [
      "This task has an imminent deadline and is critical for business operations.",
      "Multiple stakeholders are waiting on this deliverable.",
      "The task is important but can be scheduled around higher priorities.",
      "This item isn't time-sensitive and has minimal dependencies.",
      "This task can be deferred until resources are more available."
    ];

    // Show processing animation for 1.5 seconds before updating
    setIsAIProcessing(true);
    setShowAIReasons(false);
    setIsValidating(false);

    setTimeout(() => {
      // Generate new priorities with reasoning
      const newReasons: AIPriorityReason[] = [];

      const reprioritizedTasks = tasks.map(task => {
        // Decide on a new priority based on various factors
        // For demo purposes, we'll make this somewhat deterministic
        let newPriorityIndex = 0;

        // Due date factor (closer = higher priority)
        const daysUntilDue = Math.max(0, Math.floor((new Date(task.dueDate).getTime() - Date.now()) / (1000 * 3600 * 24)));

        if (daysUntilDue > 14) newPriorityIndex = 4; // none
        else if (daysUntilDue > 7) newPriorityIndex = 3; // low
        else if (daysUntilDue > 3) newPriorityIndex = 2; // medium
        else if (daysUntilDue > 1) newPriorityIndex = 1; // high
        else newPriorityIndex = 0; // urgent

        // Adjust for tags
        if (task.tags.includes('urgent')) newPriorityIndex = Math.max(0, newPriorityIndex - 1);
        if (task.tags.includes('security')) newPriorityIndex = Math.max(0, newPriorityIndex - 1);

        // Adjust for status
        if (task.status === 'blocked') newPriorityIndex = Math.min(1, newPriorityIndex); // Blocked tasks should be high or urgent
        if (task.status === 'in progress') newPriorityIndex = Math.min(2, newPriorityIndex); // Keep momentum
        if (task.status === 'done') newPriorityIndex = 4; // Completed tasks are lowest priority

        const newPriority = priorities[newPriorityIndex];

        // Create a reason that sounds AI-generated and intelligent
        let reason = reasons[newPriorityIndex];

        // Personalize the reasoning
        if (task.status === 'blocked') {
          reason = "This task is currently blocked but needs immediate attention to unblock the workflow.";
        } else if (task.tags.includes('security')) {
          reason = "Security tasks should be prioritized to minimize potential vulnerabilities.";
        } else if (task.assignees.length === 0) {
          reason = "This task needs assignees and clearer ownership to progress.";
        }

        // Store the reason if priority changed
        if (newPriority !== task.priority) {
          newReasons.push({
            taskId: task.id,
            oldPriority: task.priority,
            newPriority,
            reason
          });
        }

        return {
          ...task,
          priority: newPriority
        };
      });

      setAIReasons(newReasons);
      setPendingTasks(reprioritizedTasks);
      setIsAIProcessing(false);
      setIsValidating(true);
    }, 1500);
  };

  const handleAcceptPriorities = () => {
    // Apply AI priorities to tasks
    const updatedTasks = tasks.map(task => ({
      ...task,
      priority: aiPriorities[task.id] || task.priority
    }));

    setTasks(updatedTasks);
    setPrioritizationStatus({
      ...prioritizationStatus,
      isConfirmed: true
    });
    setIsValidating(false);

    // We keep showing the reasons for the changes
    setShowAIReasons(true);

    // In a real app, you would save these to the backend
    console.log('Saved new priorities to backend');
  };

  const handleRejectPriorities = () => {
    setIsValidating(false);
  };

  const handleCustomPrioritization = () => {
    if (!validationPrompt.trim()) return;

    // Run prioritization with custom prompt
    runAIPrioritization(validationPrompt);
    setIsValidating(false);
    setPrioritizationStatus({
      ...prioritizationStatus,
      isConfirmed: false
    });
    setValidationPrompt('');
  };

  // Function to handle blocker detection
  const handleBlockerDetection = () => {
    setBlockerDetection({
      isOpen: true,
      lastDetectionDate: new Date().toISOString().split('T')[0]
    });
  };

  // Function to close blocker detection popup
  const handleCloseBlockerDetection = () => {
    setBlockerDetection({
      ...blockerDetection,
      isOpen: false
    });
  };

  // Function to run AI prioritization
  const runAIPrioritization = (customPrompt: string = '') => {
    // Show processing animation
    setIsAIProcessing(true);
    setShowAIReasons(false);

    setTimeout(() => {
      // Generate new priorities with reasoning
      const newReasons: AIPriorityReason[] = [];
      const newAIPriorities: Record<string, TaskPriority> = {};

      // Apply prioritization logic
      tasks.forEach(task => {
        // Decide on a new priority based on various factors
        // For demo purposes, we'll make this somewhat deterministic
        let newPriority: TaskPriority = task.priority;

        // Due date factor (closer = higher priority)
        const daysUntilDue = Math.max(0, Math.floor((new Date(task.dueDate).getTime() - Date.now()) / (1000 * 3600 * 24)));

        if (daysUntilDue > 14) newPriority = 'none';
        else if (daysUntilDue > 7) newPriority = 'low';
        else if (daysUntilDue > 3) newPriority = 'medium';
        else if (daysUntilDue > 1) newPriority = 'high';
        else newPriority = 'urgent';

        // Adjust for tags
        if (task.tags.includes('urgent')) newPriority = priorityUp(newPriority);
        if (task.tags.includes('security')) newPriority = priorityUp(newPriority);

        // Adjust for status
        if (task.status === 'blocked') newPriority = Math.min(1, priorityOrder[newPriority]) === 1 ? 'urgent' : 'high';
        if (task.status === 'in progress') newPriority = priorityOrder[newPriority] <= 2 ? newPriority : 'medium';
        if (task.status === 'done') newPriority = 'none';

        // Custom prompt adjustments
        if (customPrompt) {
          const promptLower = customPrompt.toLowerCase();

          if (promptLower.includes('urgent') && task.status !== 'done') {
            newPriority = priorityUp(newPriority);
          }

          if (promptLower.includes('relax') || promptLower.includes('lower')) {
            newPriority = priorityDown(newPriority);
          }

          // Direct mentions of specific tasks
          if (task.name.toLowerCase().split(' ').some(word => promptLower.includes(word)) &&
              promptLower.includes('prioritize')) {
            newPriority = 'urgent';
          }
        }

        // Store the priority and reason if different
        newAIPriorities[task.id] = newPriority;

        if (newPriority !== task.priority) {
          // Create a reason that sounds AI-generated and intelligent
          let reason = getReason(task, newPriority, daysUntilDue);

          newReasons.push({
            taskId: task.id,
            oldPriority: task.priority,
            newPriority,
            reason
          });
        }
      });

      setAIReasons(newReasons);
      setAIPriorities(newAIPriorities);
      setIsAIProcessing(false);
      setShowAIReasons(true);
    }, 1500);
  };

  // Helper functions for priority adjustment
  const priorityUp = (priority: TaskPriority): TaskPriority => {
    const priorities: TaskPriority[] = ['urgent', 'high', 'medium', 'low', 'none'];
    const currentIndex = priorities.indexOf(priority);
    return currentIndex > 0 ? priorities[currentIndex - 1] : priority;
  };

  const priorityDown = (priority: TaskPriority): TaskPriority => {
    const priorities: TaskPriority[] = ['urgent', 'high', 'medium', 'low', 'none'];
    const currentIndex = priorities.indexOf(priority);
    return currentIndex < priorities.length - 1 ? priorities[currentIndex + 1] : priority;
  };

  // Generate reasons based on the task attributes
  const getReason = (task: Task, newPriority: TaskPriority, daysUntilDue: number): string => {
    if (task.status === 'blocked') {
      return "This task is currently blocked but needs immediate attention to unblock the workflow.";
    } else if (task.tags.includes('security')) {
      return "Security tasks should be prioritized to minimize potential vulnerabilities.";
    } else if (task.assignees.length === 0) {
      return "This task needs assignees and clearer ownership to progress.";
    } else if (daysUntilDue <= 1) {
      return "The deadline is imminent (within 24 hours), requiring urgent attention.";
    } else if (daysUntilDue <= 3) {
      return "This task has a near-term deadline within the next few days.";
    } else if (daysUntilDue <= 7) {
      return "The task is due within a week and should be addressed soon.";
    } else {
      return "This task has a comfortable timeline and can be scheduled accordingly.";
    }
  };

  // Generate blocker detection HTML report
  const generateBlockerReport = (): string => {
    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Blocker Detection Report</title>
  <style>
    :root {
      --primary: #111827;
      --secondary: #1f2937;
      --tertiary: #374151;
      --accent: #6366f1;
      --border-color: rgba(255, 255, 255, 0.1);
      --text: #ffffff;
      --text-secondary: #9ca3af;
      --red-gradient: linear-gradient(135deg, #f87171, #ef4444, #dc2626);
      --orange-gradient: linear-gradient(135deg, #fdba74, #fb923c, #f97316);
      --purple-gradient: linear-gradient(135deg, #c084fc, #a855f7, #8b5cf6);
    }
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    body {
      background-color: var(--primary);
      color: var(--text);
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
      line-height: 1.5;
      padding: 2rem;
    }
    .report-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 2rem;
      padding-bottom: 1rem;
      border-bottom: 1px solid var(--border-color);
    }
    .report-title {
      font-size: 1.5rem;
      font-weight: 700;
    }
    .report-date {
      color: var(--text-secondary);
      font-size: 0.875rem;
    }
    .bento-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
      gap: 1.5rem;
    }
    .bento-card {
      background-color: var(--secondary);
      border-radius: 0.75rem;
      padding: 1.5rem;
      border: 1px solid var(--border-color);
      position: relative;
      overflow: hidden;
    }
    .bento-card.critical {
      border-top: 3px solid #ef4444;
    }
    .bento-card.warning {
      border-top: 3px solid #f97316;
    }
    .bento-card.info {
      border-top: 3px solid #6366f1;
    }
    .bento-card::before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      width: 150px;
      height: 150px;
      background: var(--accent);
      opacity: 0.05;
      border-radius: 50%;
      transform: translate(50%, -50%);
    }
    .card-header {
      display: flex;
      gap: 0.75rem;
      align-items: center;
      margin-bottom: 1rem;
    }
    .icon-wrapper {
      width: 2.5rem;
      height: 2.5rem;
      border-radius: 0.5rem;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .icon-wrapper.critical {
      background: var(--red-gradient);
    }
    .icon-wrapper.warning {
      background: var(--orange-gradient);
    }
    .icon-wrapper.info {
      background: var(--purple-gradient);
    }
    .card-title {
      font-weight: 600;
      font-size: 1.125rem;
    }
    .task-list {
      margin-top: 1rem;
    }
    .task-item {
      padding: 0.75rem;
      border-radius: 0.5rem;
      background-color: var(--tertiary);
      margin-bottom: 0.75rem;
    }
    .task-name {
      font-weight: 500;
      margin-bottom: 0.25rem;
    }
    .task-detail {
      font-size: 0.875rem;
      color: var(--text-secondary);
    }
    .progress-bar {
      height: 0.5rem;
      width: 100%;
      background-color: var(--tertiary);
      border-radius: 0.25rem;
      margin-top: 1rem;
      overflow: hidden;
    }
    .progress {
      height: 100%;
      background: var(--purple-gradient);
      width: 65%;
    }
    .insights {
      margin-top: 1.5rem;
      padding-top: 1.5rem;
      border-top: 1px solid var(--border-color);
    }
    .insights-title {
      font-weight: 600;
      margin-bottom: 0.75rem;
    }
    .insight-item {
      padding-left: 1rem;
      border-left: 2px solid var(--accent);
      margin-bottom: 0.75rem;
    }
    .assignee-list {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
      margin-top: 0.5rem;
    }
    .assignee {
      background-color: var(--tertiary);
      padding: 0.25rem 0.5rem;
      border-radius: 0.25rem;
      font-size: 0.75rem;
    }
    .status-indicator {
      display: inline-block;
      width: 0.5rem;
      height: 0.5rem;
      border-radius: 50%;
      margin-right: 0.25rem;
    }
    .status-blocked {
      background-color: #ef4444;
    }
    .status-todo {
      background-color: #9ca3af;
    }
    .status-inprogress {
      background-color: #6366f1;
    }
    /* Media queries for better responsiveness */
    @media (max-width: 768px) {
      .bento-grid {
        grid-template-columns: 1fr;
      }
    }
    @media (min-width: 769px) and (max-width: 1024px) {
      .bento-grid {
        grid-template-columns: repeat(2, 1fr);
      }
    }
    @media (min-width: 1025px) {
      .bento-grid {
        grid-template-columns: repeat(3, 1fr);
      }
    }
  </style>
</head>
<body>
  <div class="report-header">
    <div class="report-title">Blocker Detection Report</div>
    <div class="report-date">Thursday, June 15, 2023</div>
  </div>

  <div class="bento-grid">
    <div class="bento-card critical">
      <div class="card-header">
        <div class="icon-wrapper critical">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" width="20" height="20">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
        </div>
        <div class="card-title">Blocked Tasks (1)</div>
      </div>
      <div class="task-list">
        <div class="task-item">
          <div class="task-name">Sprint planning</div>
          <div class="task-detail">Due: Jun 15
            <div class="assignee-list">
              <span class="assignee">Taylor Johnson</span>
              <span class="assignee">Jamie Wilson</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="bento-card warning">
      <div class="card-header">
        <div class="icon-wrapper warning">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" width="20" height="20">
            <path stroke-linecap="round" stroke-linejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
          </svg>
        </div>
        <div class="card-title">Potential Blockers (2)</div>
      </div>
      <div class="task-list">
        <div class="task-item">
          <div class="task-name">Add authentication system</div>
          <div class="task-detail"><span class="status-indicator status-todo"></span>Status: to do</div>
          <div class="task-detail">This task may be blocking other work.</div>
        </div>
        <div class="task-item">
          <div class="task-name">Design product dashboard</div>
          <div class="task-detail"><span class="status-indicator status-inprogress"></span>Status: in progress</div>
          <div class="task-detail">This task may be blocking other work.</div>
        </div>
      </div>
    </div>

    <div class="bento-card info">
      <div class="card-header">
        <div class="icon-wrapper info">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" width="20" height="20">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
          </svg>
        </div>
        <div class="card-title">Unassigned Tasks (1)</div>
      </div>
      <div class="task-list">
        <div class="task-item">
          <div class="task-name">Update user documentation</div>
          <div class="task-detail">Due: Jun 20</div>
          <div class="task-detail">This task needs assignees to progress.</div>
        </div>
      </div>
    </div>

    <div class="bento-card critical">
      <div class="card-header">
        <div class="icon-wrapper critical">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" width="20" height="20">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div class="card-title">Urgent Unstarted Tasks (2)</div>
      </div>
      <div class="task-list">
        <div class="task-item">
          <div class="task-name">Fix critical login bug</div>
          <div class="task-detail">Due: Jun 16
            <div class="assignee-list">
              <span class="assignee">Casey Brown</span>
            </div>
          </div>
        </div>
        <div class="task-item">
          <div class="task-name">Implement responsive design</div>
          <div class="task-detail">Due: Jun 17
            <div class="assignee-list">
              <span class="assignee">Alex Smith</span>
              <span class="assignee">Jordan Williams</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="bento-card info">
      <div class="card-header">
        <div class="icon-wrapper info">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" width="20" height="20">
            <path stroke-linecap="round" stroke-linejoin="round" d="M11.35 3.836c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m8.9-4.414c.376.023.75.05 1.124.08 1.131.094 1.976 1.057 1.976 2.192V16.5A2.25 2.25 0 0118 18.75h-2.25m-7.5-10.5H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V18.75m-7.5-10.5h6.375c.621 0 1.125.504 1.125 1.125v9.375m-8.25-3l1.5 1.5 3-3.75" />
          </svg>
        </div>
        <div class="card-title">Project Health</div>
      </div>
      <div>
        <div class="task-detail">Overall completion</div>
        <div class="progress-bar">
          <div class="progress"></div>
        </div>
        <div class="task-detail" style="text-align: right; margin-top: 0.25rem;">5 / 8 tasks (65%)</div>
      </div>
      <div class="insights">
        <div class="insights-title">AI Insights</div>
        <div class="insight-item">12.5% of tasks are currently blocked. Consider prioritizing potential blocker tasks.</div>
        <div class="insight-item">1 task requires assignment to team members for accountability.</div>
        <div class="insight-item">There are 2 urgent tasks that haven't been started yet. Consider immediate action.</div>
      </div>
    </div>

    <div class="bento-card warning">
      <div class="card-header">
        <div class="icon-wrapper warning">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" width="20" height="20">
            <path stroke-linecap="round" stroke-linejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
          </svg>
        </div>
        <div class="card-title">Team Load Analysis</div>
      </div>
      <div class="task-list">
        <div class="task-item">
          <div class="task-name">Alex Smith</div>
          <div class="task-detail">2 active tasks (1 blocked)</div>
          <div class="progress-bar">
            <div class="progress" style="width: 75%;"></div>
          </div>
        </div>
        <div class="task-item">
          <div class="task-name">Casey Brown</div>
          <div class="task-detail">3 active tasks (0 blocked)</div>
          <div class="progress-bar">
            <div class="progress" style="width: 90%;"></div>
          </div>
        </div>
        <div class="task-item">
          <div class="task-name">Jordan Williams</div>
          <div class="task-detail">1 active task (0 blocked)</div>
          <div class="progress-bar">
            <div class="progress" style="width: 40%;"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</body>
</html>`;
  };

  return (
    <div className="min-h-screen bg-secondary text-white relative">
      {/* Animation CSS */}
      <style>
        {`
          @keyframes checkmark {
            0% { transform: scale(0); opacity: 0; }
            50% { transform: scale(1.2); opacity: 1; }
            100% { transform: scale(1); opacity: 1; }
          }
          .check-animation {
            animation: checkmark 0.3s ease-in-out forwards;
          }

          @keyframes taskComplete {
            0% { opacity: 1; }
            30% { opacity: 0.7; }
            100% { opacity: 1; background-color: rgba(16, 185, 129, 0.05); }
          }
          .task-complete-animation {
            animation: taskComplete 0.5s ease-in-out forwards;
          }

          @keyframes pulse {
            0% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.05); opacity: 0.8; }
            100% { transform: scale(1); opacity: 1; }
          }

          @keyframes sparkle {
            0% { transform: scale(0) rotate(0deg); opacity: 0; }
            50% { transform: scale(1) rotate(180deg); opacity: 1; }
            100% { transform: scale(0) rotate(360deg); opacity: 0; }
          }

          .sparkle {
            position: absolute;
            width: 6px;
            height: 6px;
            border-radius: 50%;
            background-color: white;
            pointer-events: none;
          }

          .sparkle:nth-child(1) {
            top: -5px;
            left: 10px;
            animation: sparkle 1.5s ease-in-out infinite;
          }

          .sparkle:nth-child(2) {
            top: 5px;
            left: -5px;
            animation: sparkle 1.8s ease-in-out 0.2s infinite;
          }

          .sparkle:nth-child(3) {
            top: 20px;
            left: -3px;
            animation: sparkle 1.6s ease-in-out 0.4s infinite;
          }

          .sparkle:nth-child(4) {
            top: -3px;
            right: 10px;
            animation: sparkle 1.7s ease-in-out 0.6s infinite;
          }

          .sparkle:nth-child(5) {
            bottom: 5px;
            right: -3px;
            animation: sparkle 1.9s ease-in-out 0.8s infinite;
          }

          .ai-button {
            background: linear-gradient(45deg, #4f46e5, #8b5cf6, #ec4899);
            background-size: 200% 200%;
            animation: pulse 2s ease-in-out infinite, gradient 3s ease infinite;
          }

          @keyframes gradient {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }

          @keyframes reasonAppear {
            0% { opacity: 0; transform: translateY(-10px); }
            100% { opacity: 1; transform: translateY(0); }
          }

          .reason-appear {
            animation: reasonAppear 0.3s ease-in-out forwards;
          }

          .priority-changed {
            position: relative;
            overflow: visible;
          }

          .priority-changed::after {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: linear-gradient(45deg, rgba(139, 92, 246, 0.1), rgba(236, 72, 153, 0.1));
            border-radius: inherit;
            z-index: -1;
            opacity: 0;
            animation: pulse 2s ease-in-out 1;
          }

          /* AI Priority column styling */
          .ai-priority-column {
            position: relative;
            background: linear-gradient(45deg, rgba(79, 70, 229, 0.05), rgba(139, 92, 246, 0.05));
            transition: all 0.2s ease-in-out;
          }

          .ai-priority-column.clickable:hover {
            background: linear-gradient(45deg, rgba(79, 70, 229, 0.1), rgba(139, 92, 246, 0.1));
            box-shadow: inset 0 0 0 1px rgba(139, 92, 246, 0.3);
          }

          .ai-priority-column::before {
            content: "";
            position: absolute;
            inset: 0;
            border-radius: 0.5rem;
            padding: 1.5px;
            background: linear-gradient(45deg, #4f46e5, #8b5cf6, #ec4899);
            -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
            -webkit-mask-composite: xor;
            mask-composite: exclude;
            opacity: 0.7;
            animation: borderGlow 2s infinite alternate;
          }

          /* Add subtle circuit-like pattern to AI column */
          .ai-priority-column::after {
            content: "";
            position: absolute;
            inset: 0;
            background-image:
              radial-gradient(circle at 40px 40px, rgba(99, 102, 241, 0.1) 0, rgba(99, 102, 241, 0.1) 1px, transparent 1px),
              radial-gradient(circle at 40px 40px, rgba(139, 92, 246, 0.07) 0, rgba(139, 92, 246, 0.07) 1.5px, transparent 1.5px);
            background-size: 10px 10px;
            background-position: 0 0;
            opacity: 0.5;
            z-index: -1;
          }

          @keyframes borderGlow {
            0% { opacity: 0.5; }
            100% { opacity: 1; }
          }

          .ai-priority-badge, .ai-priority-normal {
            position: relative;
          }

          .ai-priority-badge::after, .ai-priority-normal::after {
            content: none;
          }

          .ai-priority-badge:hover::before, .ai-priority-normal:hover::before {
            content: none;
          }

          /* Add indicator to show that the cell is interactive */
          .ai-priority-column.clickable::after {
            content: "";
            position: absolute;
            bottom: 5px;
            right: 5px;
            width: 6px;
            height: 6px;
            border-radius: 50%;
            background: rgba(139, 92, 246, 0.5);
            opacity: 0.8;
            transition: all 0.2s ease-in-out;
          }

          .ai-priority-column.clickable:hover::after {
            opacity: 1;
            width: 8px;
            height: 8px;
            background: rgba(139, 92, 246, 0.8);
          }

          .ai-priority-badge::after, .ai-priority-normal::after {
            content: "Click for AI reasoning";
            position: absolute;
            bottom: calc(100% + 5px);
            left: 50%;
            transform: translateX(-50%) translateY(5px);
            background: rgba(30, 30, 46, 0.95);
            color: #fff;
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 10px;
            white-space: nowrap;
            opacity: 0;
            pointer-events: none;
            transition: all 0.2s ease;
            box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
            border: 1px solid rgba(99, 102, 241, 0.3);
            z-index: 10;
          }

          .ai-priority-badge:hover::after, .ai-priority-normal:hover::after {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }

          /* Add a small indicator icon */
          .ai-priority-badge:hover::before, .ai-priority-normal:hover::before {
            content: "";
            position: absolute;
            bottom: calc(100% + 3px);
            left: 50%;
            transform: translateX(-50%) rotate(45deg);
            width: 6px;
            height: 6px;
            background-color: rgba(30, 30, 46, 0.95);
            border-right: 1px solid rgba(99, 102, 241, 0.3);
            border-bottom: 1px solid rgba(99, 102, 241, 0.3);
            z-index: 11;
          }

          .ai-priority-badge {
            position: relative;
            z-index: 1;
            cursor: pointer;
          }

          .ai-priority-badge:hover {
            transform: translateY(-1px);
          }

          .ai-priority-badge::before {
            content: "";
            position: absolute;
            inset: -3px;
            border-radius: 9999px;
            background: linear-gradient(45deg, #4f46e5, #8b5cf6, #ec4899);
            z-index: -1;
            opacity: 0;
            filter: blur(8px);
            animation: glow 1.5s ease-in-out infinite alternate;
          }

          @keyframes glow {
            0% { opacity: 0.3; transform: scale(0.95); }
            100% { opacity: 0.8; transform: scale(1.05); }
          }

          .reason-bubble {
            position: absolute;
            right: 10px;
            background: linear-gradient(45deg, #3b2c92, #6366f1);
            border: 1px solid rgba(255, 255, 255, 0.2);
            border-radius: 0.5rem;
            padding: 0.75rem;
            box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.5);
            z-index: 10;
            max-width: 300px;
            opacity: 0;
            animation: reasonAppear 0.3s ease-in-out 0.1s forwards;
          }

          .reason-bubble::before {
            content: "";
            position: absolute;
            top: 50%;
            right: -8px;
            transform: translateY(-50%) rotate(45deg);
            width: 16px;
            height: 16px;
            background: linear-gradient(45deg, #3b2c92, #6366f1);
            border-right: 1px solid rgba(255, 255, 255, 0.2);
            border-top: 1px solid rgba(255, 255, 255, 0.2);
          }

          .prompt-input {
            background: rgba(99, 102, 241, 0.1);
            border: 1px solid rgba(139, 92, 246, 0.3);
            transition: all 0.2s ease-in-out;
          }

          .prompt-input:focus {
            background: rgba(99, 102, 241, 0.15);
            border-color: rgba(139, 92, 246, 0.5);
            box-shadow: 0 0 0 2px rgba(139, 92, 246, 0.2);
          }

          .validation-card {
            background: linear-gradient(45deg, rgba(79, 70, 229, 0.2), rgba(139, 92, 246, 0.2));
            backdrop-filter: blur(10px);
            border: 1px solid rgba(139, 92, 246, 0.3);
            box-shadow: 0 15px 30px -10px rgba(0, 0, 0, 0.5);
          }

          @keyframes highlight {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }

          .highlight-button {
            background: linear-gradient(45deg, #4f46e5, #8b5cf6, #4f46e5);
            background-size: 200% 200%;
            animation: highlight 3s ease infinite;
            border: none;
          }

          /* Blocker button animation */
          .blocker-button {
            background: linear-gradient(45deg, #f97316, #ef4444, #f97316);
            background-size: 200% 200%;
            animation: highlight 3s ease infinite;
            box-shadow: 0 0 15px rgba(249, 115, 22, 0.3);
            position: relative;
            overflow: hidden;
          }

          .blocker-button::after {
            content: "";
            position: absolute;
            top: -50%;
            left: -50%;
            width: 200%;
            height: 200%;
            background: radial-gradient(circle, rgba(255, 255, 255, 0.3) 0%, transparent 70%);
            opacity: 0;
            transition: opacity 0.3s;
          }

          .blocker-button:hover::after {
            opacity: 1;
          }

          /* Add this to the CSS styles */
          .ai-priority-normal {
            box-shadow: 0 0 0 1px rgba(139, 92, 246, 0.3);
            transition: all 0.2s ease-in-out;
            cursor: pointer;
          }

          .ai-priority-normal:hover {
            box-shadow: 0 0 0 2px rgba(139, 92, 246, 0.5), 0 0 15px rgba(139, 92, 246, 0.3);
            transform: translateY(-1px);
          }

          .ai-priority-badge {
            position: relative;
            z-index: 1;
            cursor: pointer;
          }

          .ai-priority-badge:hover {
            transform: translateY(-1px);
          }

          @keyframes twinkle {
            0%, 100% { opacity: 0; transform: scale(0.5); }
            50% { opacity: 1; transform: scale(1.2); }
          }

          .animate-twinkle {
            animation: twinkle 2s ease-in-out infinite;
          }
        `}
      </style>

      {/* Background Effect */}
      <div className="absolute inset-0 overflow-hidden z-0">
        <div className="absolute -top-1/2 -left-1/4 w-3/4 h-3/4 bg-accent/5 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-1/2 -right-1/4 w-3/4 h-3/4 bg-blue-500/5 rounded-full blur-3xl"></div>
        <div className="absolute top-1/4 left-1/3 w-1/3 h-1/3 bg-purple-500/5 rounded-full blur-3xl"></div>
      </div>

      <header className="bg-primary/80 backdrop-blur-md px-6 py-4 border-b border-border-color sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <h1 className="text-xl font-bold text-white">Task Management</h1>
          <button
            onClick={() => navigate('/chat')}
            className="text-sm px-4 py-2 rounded-md bg-accent hover:bg-accent/90 transition-colors shadow-lg shadow-accent/20"
          >
            Back to Chat
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 relative z-1">
        <div className="bg-primary/90 rounded-2xl shadow-2xl overflow-hidden backdrop-blur-xl border border-white/5 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-accent/5 opacity-50"></div>
          <div className="p-8 relative">
            <div className="mb-6 pb-6 border-b border-border-color flex justify-between items-center">
              <div>
                <h2 className="text-3xl font-extrabold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                  Task Management
                </h2>
                <p className="text-gray-400">ClickUp-style task management with smart prioritization</p>
              </div>
              <div className="flex items-center gap-4">
                <button
                  onClick={handleBlockerDetection}
                  className="flex items-center gap-2 px-4 py-2 blocker-button rounded-lg transition-colors text-white text-sm font-medium shadow-lg"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                  </svg>
                  Blockers
                </button>
                <button
                  onClick={handleAddNewTask}
                  className="flex items-center gap-2 px-4 py-2 bg-accent hover:bg-accent/90 rounded-lg transition-colors text-white text-sm font-medium shadow-lg shadow-accent/20"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>
                  Add Task
                </button>

                <div className="bg-accent/20 p-3 rounded-full shadow-lg shadow-accent/10">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-accent">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                  </svg>
                </div>
              </div>
            </div>

            {/* AI Validation UI */}
            <AnimatePresence>
              {isValidating && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
                >
                  <div className="validation-card max-w-2xl w-full rounded-xl p-6 space-y-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="bg-indigo-500/20 p-2 rounded-lg">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-indigo-400">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09Z" />
                          </svg>
                        </div>
                        <h3 className="font-bold text-xl">Custom Prioritization</h3>
                      </div>
                      <button
                        onClick={handleRejectPriorities}
                        className="text-gray-400 hover:text-white p-1.5 hover:bg-gray-800/50 rounded-lg transition-colors"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    <div>
                      <p className="text-gray-300 mb-4">
                        Provide guidance for how you'd like tasks to be prioritized. For example:
                      </p>

                      <div className="space-y-2 mb-4">
                        <div className="bg-indigo-900/20 p-2 rounded-lg text-sm text-gray-300">
                          "Prioritize security-related tasks above all else"
                        </div>
                        <div className="bg-indigo-900/20 p-2 rounded-lg text-sm text-gray-300">
                          "Relax deadlines for tasks assigned to Alex"
                        </div>
                        <div className="bg-indigo-900/20 p-2 rounded-lg text-sm text-gray-300">
                          "Make current in-progress tasks high priority"
                        </div>
                      </div>

                      <div className="relative">
                        <input
                          type="text"
                          value={validationPrompt}
                          onChange={(e) => setValidationPrompt(e.target.value)}
                          placeholder="Describe how you want tasks prioritized..."
                          className="w-full px-4 py-3 rounded-lg prompt-input text-white placeholder-gray-500 outline-none"
                        />
                        <button
                          onClick={handleCustomPrioritization}
                          disabled={!validationPrompt.trim()}
                          className={`absolute right-2 top-1/2 transform -translate-y-1/2 p-1.5 rounded-lg ${
                            validationPrompt.trim() ? 'bg-indigo-500 hover:bg-indigo-600 text-white' : 'bg-gray-700 text-gray-400'
                          } transition-colors`}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>


            {loading ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent"></div>
              </div>
            ) : (
              <>
                {/* Task Completion Stats */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-2">
                    <div className="text-sm font-medium text-gray-400">
                      Completion: {tasks.filter(t => t.status === 'done').length} of {tasks.length} tasks
                    </div>
                    <div className="text-sm font-medium text-gray-400">
                      {Math.round((tasks.filter(t => t.status === 'done').length / tasks.length) * 100)}% Complete
                    </div>
                  </div>
                  <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-accent to-green-500 transition-all duration-500"
                      style={{ width: `${tasks.length ? (tasks.filter(t => t.status === 'done').length / tasks.length) * 100 : 0}%` }}
                    ></div>
                  </div>
                </div>


            {/* AI Reasoning Summary */}
            <AnimatePresence>
              {showAIReasons && aiReasons.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="mb-6 bg-gradient-to-r from-indigo-900/60 to-purple-900/60 rounded-xl p-5 border border-indigo-500/30 shadow-lg"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="bg-indigo-500/20 p-2 rounded-lg">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-indigo-400">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09Z" />
                        </svg>
                      </div>
                      <h3 className="font-bold text-lg">AI Priority Assistant</h3>
                    </div>
                    <button
                      onClick={() => setShowAIReasons(false)}
                      className="text-gray-400 hover:text-white"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <p className="text-indigo-200 mb-4">
                    I've analyzed your tasks and reprioritized {aiReasons.length} tasks based on deadlines, dependencies, and project impact.
                  </p>

                  <div className="max-h-[200px] overflow-y-auto custom-scrollbar pr-2">
                    <div className="space-y-2">
                      {aiReasons.map((reasonItem, index) => (
                        <motion.div
                          key={reasonItem.taskId}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="flex items-start gap-3 bg-indigo-900/40 p-3 rounded-lg border border-indigo-500/20"
                        >
                          <div className={`flex-shrink-0 mt-1 h-4 w-4 rounded-full ${priorityColors[reasonItem.newPriority]}`}></div>
                          <div>
                            <div className="font-medium text-sm flex items-center gap-2">
                              {tasks.find(t => t.id === reasonItem.taskId)?.name}
                              <span className="text-xs font-normal text-gray-400">
                                {reasonItem.oldPriority} → {reasonItem.newPriority}
                              </span>
                            </div>
                            <p className="text-xs text-gray-300 mt-0.5">{reasonItem.reason}</p>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>



                {/* Prioritization action buttons - moved here */}
                {!prioritizationStatus.isConfirmed && !isAIProcessing && (
                  <div className="mb-6 mt-8 flex items-center justify-end gap-3  p-3 rounded-xl">

                    <button
                      onClick={handleAcceptPriorities}
                      className="flex items-center gap-2 px-4 py-2 highlight-button rounded-lg text-white text-sm font-medium shadow-lg"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                      </svg>
                      Confirm Changes
                    </button>
                    <button
                      onClick={() => setIsValidating(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm font-medium transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09Z" />
                      </svg>
                      Reprioritize
                    </button>
                  </div>
                )}
                </motion.div>
              )}
            </AnimatePresence>



                {isAIProcessing && (
                  <div className="mb-6 flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-indigo-900/30 text-white text-sm font-medium border border-indigo-500/20">
                    <svg className="animate-spin h-5 w-5 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing Priorities...
                  </div>
                )}

                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b border-border-color text-left">
                        <th className="p-3 text-gray-400 font-medium">Task</th>
                        <th className="p-3 text-gray-400 font-medium text-center">Status</th>
                        <th className="p-3 text-gray-400 font-medium text-center">Due Date</th>
                        <th className="p-3 text-gray-400 font-medium text-center">Assignees</th>
                        <th className="p-3 text-gray-400 font-medium text-center">Current Priority</th>
                        {!prioritizationStatus.isConfirmed && (
                          <th className="p-3 text-gray-400 font-medium relative text-center">
                            <div className="flex items-center justify-center gap-1">
                              <span className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 bg-clip-text text-transparent font-semibold">AI Priority</span>
                              <div className="relative w-5 h-5 flex items-center justify-center">
                                <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-full opacity-20 animate-pulse"></div>
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-indigo-400">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09Z" />
                                </svg>
                              </div>
                            </div>

                            {/* Add sparkle animations */}
                            <div className="absolute -right-2 -top-1">
                              <div className="relative w-16 h-16 opacity-70">
                                <div className="absolute h-1 w-1 rounded-full bg-indigo-400 animate-twinkle" style={{ top: '20%', left: '40%', animationDelay: '0s' }}></div>
                                <div className="absolute h-1.5 w-1.5 rounded-full bg-purple-400 animate-twinkle" style={{ top: '30%', left: '65%', animationDelay: '0.3s' }}></div>
                                <div className="absolute h-0.5 w-0.5 rounded-full bg-pink-400 animate-twinkle" style={{ top: '60%', left: '25%', animationDelay: '0.7s' }}></div>
                                <div className="absolute h-1 w-1 rounded-full bg-indigo-400 animate-twinkle" style={{ top: '10%', left: '70%', animationDelay: '1.1s' }}></div>
                                <div className="absolute h-0.5 w-0.5 rounded-full bg-purple-400 animate-twinkle" style={{ top: '40%', left: '20%', animationDelay: '1.5s' }}></div>
                              </div>
                            </div>
                          </th>
                        )}
                        <th className="p-3 text-gray-400 font-medium text-center">Tags</th>
                        <th className="p-3 text-gray-400 font-medium text-center"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortTasks(tasks).map(task => {
                        const hasReasonBubble = showAIReasons && aiReasons.some(r => r.taskId === task.id);
                        const aiPriority = aiPriorities[task.id] || task.priority;
                        const priorityChanged = aiPriority !== task.priority;

                        return (
                          <tr
                            key={task.id}
                            className={`border-b border-border-color hover:bg-tertiary cursor-pointer transition-colors ${
                              task.status === 'done' ? 'task-complete-animation' : ''
                            } ${hasReasonBubble ? 'priority-changed' : ''}`}
                            onClick={() => handleTaskClick(task)}
                          >
                            <td className="p-3">
                              <div className="flex items-center gap-3">
                                <button
                                  onClick={(e) => handleTaskCompletion(e, task.id)}
                                  className={`w-6 h-6 flex-shrink-0 rounded-full border-2 ${
                                    task.status === 'done'
                                      ? 'bg-green-500 border-green-600 text-white'
                                      : 'bg-tertiary border-gray-600 hover:border-accent hover:bg-gray-700'
                                  } flex items-center justify-center transition-all duration-200`}
                                  title={task.status === 'done' ? "Mark as incomplete" : "Mark as complete"}
                                >
                                  {task.status === 'done' && (
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-4 h-4 check-animation">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                                    </svg>
                                  )}
                                </button>
                                <div className="font-medium">{task.name}</div>
                              </div>
                            </td>
                            <td className="p-3 text-center">
                              <div className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[task.status]}`}>
                                {task.status}
                              </div>
                            </td>
                            <td className="p-3 text-sm text-center">
                              {formatDate(task.dueDate)}
                            </td>
                            <td className="p-3 text-center">
                              <div className="flex justify-center -space-x-2">
                                {task.assignees.map(assignee => (
                                  <div
                                    key={assignee.id}
                                    className={`h-8 w-8 rounded-full ${assignee.inChatbotGG ? 'border-2 border-accent' : 'border-2 border-orange-500'} bg-gray-700 flex items-center justify-center overflow-hidden`}
                                    title={`${assignee.name}${!assignee.inChatbotGG ? ' (External)' : ''}`}
                                  >
                                    {assignee.avatar ? (
                                      <img src={assignee.avatar} alt={assignee.name} className="h-full w-full object-cover" />
                                    ) : (
                                      <span className="text-xs font-semibold">
                                        {assignee.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </td>
                            <td className="p-3 text-center">
                              <div className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${priorityColors[task.priority]}`}>
                                {task.priority}
                              </div>
                            </td>
                            {!prioritizationStatus.isConfirmed && (
                              <td
                                className={`p-3 ai-priority-column ${hasReasonBubble ? 'clickable' : ''} text-center`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (hasReasonBubble) {
                                    // Find the reason
                                    const reason = aiReasons.find(r => r.taskId === task.id);
                                    if (reason) {
                                      // Show reason in a tooltip/modal
                                      setSelectedReason(reason);
                                    }
                                  }
                                }}
                                title={hasReasonBubble ? "Click to see AI reasoning" : ""}
                                style={{ cursor: hasReasonBubble ? 'pointer' : 'default' }}
                              >
                                <div className="relative flex justify-center">
                                  <div className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${priorityColors[aiPriority]} relative ${
                                    priorityChanged ? 'border border-white/30 shadow-lg ai-priority-badge' : 'ai-priority-normal'
                                  }`}>
                                    {priorityChanged && (
                                      <span className="absolute -right-1.5 -top-1.5 flex h-3 w-3">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                                        <span className="relative inline-flex rounded-full h-3 w-3 bg-indigo-500"></span>
                                      </span>
                                    )}
                                    {aiPriority}
                                  </div>


                                </div>
                              </td>
                            )}
                            <td className="p-3 text-center">
                              <div className="flex flex-wrap gap-1 justify-center">
                                {task.tags.map(tag => (
                                  <span key={tag} className="px-2 py-0.5 rounded-full text-xs bg-gray-700 text-gray-300">
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td className="p-3 text-center">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleTaskClick(task);
                                  setIsEditing(true);
                                }}
                                className="text-gray-400 hover:text-white p-1.5 hover:bg-tertiary rounded-lg transition-colors mx-auto"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                                </svg>
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      </main>

      {/* Task Detail Popup */}
      <AnimatePresence>
        {selectedTask && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-primary rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto custom-scrollbar border border-border-color"
            >
              <div className="sticky top-0 z-10 bg-primary p-6 border-b border-border-color flex justify-between items-center backdrop-blur-md bg-opacity-95">
                <h2 className="text-xl font-bold text-white">
                  {isEditing ? "Edit Task" : "Task Details"}
                </h2>
                <div className="flex gap-2">
                  {!isEditing && (
                    <>
                      <button
                        onClick={handleEditToggle}
                        className="p-2 rounded-lg hover:bg-tertiary transition-colors"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                        </svg>
                      </button>
                      <button
                        onClick={handleDeleteTask}
                        className="p-2 rounded-lg hover:bg-red-500/20 text-red-400 transition-colors"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                        </svg>
                      </button>
                    </>
                  )}
                  <button
                    onClick={handleClosePopup}
                    className="p-2 rounded-lg hover:bg-tertiary transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="p-6">
                {isEditing ? (
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">Task Name</label>
                      <input
                        type="text"
                        value={editedTask?.name || ''}
                        onChange={(e) => handleInputChange(e, 'name')}
                        className="w-full px-4 py-2 bg-tertiary rounded-lg border border-border-color focus:border-accent outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">Description</label>
                      <textarea
                        value={editedTask?.description || ''}
                        onChange={(e) => handleInputChange(e, 'description')}
                        rows={4}
                        className="w-full px-4 py-2 bg-tertiary rounded-lg border border-border-color focus:border-accent outline-none"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1">Status</label>
                        <div className="grid grid-cols-2 gap-2">
                          {(['to do', 'in progress', 'done', 'blocked', 'review'] as TaskStatus[]).map(status => (
                            <div
                              key={status}
                              onClick={() => handleStatusChange(status)}
                              className={`px-3 py-2 rounded-lg cursor-pointer text-center text-sm font-medium transition-colors ${
                                editedTask?.status === status
                                  ? statusColors[status]
                                  : 'bg-tertiary hover:bg-gray-700'
                              }`}
                            >
                              {status}
                            </div>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1">Priority</label>
                        <div className="grid grid-cols-3 gap-2">
                          {(['urgent', 'high', 'medium', 'low', 'none'] as TaskPriority[]).map(priority => (
                            <div
                              key={priority}
                              onClick={() => handlePriorityChange(priority)}
                              className={`px-3 py-2 rounded-lg cursor-pointer text-center text-sm font-medium transition-colors ${
                                editedTask?.priority === priority
                                  ? priorityColors[priority]
                                  : 'bg-tertiary hover:bg-gray-700'
                              }`}
                            >
                              {priority}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">Due Date</label>
                      <input
                        type="date"
                        value={editedTask?.dueDate ? new Date(editedTask.dueDate).toISOString().split('T')[0] : ''}
                        onChange={(e) => handleInputChange(e, 'dueDate')}
                        className="w-full px-4 py-2 bg-tertiary rounded-lg border border-border-color focus:border-accent outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">Assignees</label>
                      <div className="grid grid-cols-2 gap-3 mt-2">
                        {dummyTeamMembers.map(member => {
                          const isAssigned = editedTask?.assignees.some(a => a.id === member.id);
                          return (
                            <div
                              key={member.id}
                              className="flex items-center space-x-3 p-2 rounded-lg bg-tertiary"
                            >
                              <div className="h-8 w-8 rounded-full bg-gray-700 flex items-center justify-center overflow-hidden border-2 border-accent">
                                {member.photo ? (
                                  <img src={member.photo} alt={member.name} className="h-full w-full object-cover" />
                                ) : (
                                  <span className="text-xs font-semibold">
                                    {member.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                                  </span>
                                )}
                              </div>
                              <span className="flex-1 text-sm">{member.name}</span>
                              <input
                                type="checkbox"
                                checked={isAssigned}
                                onChange={(e) => handleAssigneeChange(member.id, e.target.checked)}
                                className="h-4 w-4 accent-accent rounded"
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">Tags (comma separated)</label>
                      <input
                        type="text"
                        value={editedTask?.tags.join(', ') || ''}
                        onChange={handleTagsChange}
                        className="w-full px-4 py-2 bg-tertiary rounded-lg border border-border-color focus:border-accent outline-none"
                        placeholder="design, frontend, urgent"
                      />
                    </div>

                    <div className="flex justify-end space-x-3 pt-4">
                      <button
                        onClick={handleClosePopup}
                        className="px-4 py-2 bg-tertiary hover:bg-gray-700 transition-colors rounded-lg"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveChanges}
                        className="px-4 py-2 bg-accent hover:bg-accent/90 transition-colors rounded-lg"
                      >
                        Save Changes
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-2xl font-bold">{selectedTask.name}</h3>
                      <div className="flex items-center gap-4 mt-2">
                        <div className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[selectedTask.status]}`}>
                          {selectedTask.status}
                        </div>
                        <div className={`px-2.5 py-1 rounded-full text-xs font-medium ${priorityColors[selectedTask.priority]}`}>
                          {selectedTask.priority}
                        </div>
                        <div className="text-gray-400 text-sm">
                          Due: {formatDate(selectedTask.dueDate)}
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="text-sm font-medium text-gray-400 mb-2">Description</h4>
                      <p className="text-gray-300 whitespace-pre-line">
                        {selectedTask.description || 'No description provided.'}
                      </p>
                    </div>

                    <div>
                      <h4 className="text-sm font-medium text-gray-400 mb-2">Assignees</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedTask.assignees.map(assignee => (
                          <div
                            key={assignee.id}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg bg-tertiary ${
                              !assignee.inChatbotGG ? 'border border-orange-500/50' : ''
                            }`}
                          >
                            <div className={`h-8 w-8 rounded-full ${assignee.inChatbotGG ? 'border-2 border-accent' : 'border-2 border-orange-500'} bg-gray-700 flex items-center justify-center overflow-hidden`}>
                              {assignee.avatar ? (
                                <img src={assignee.avatar} alt={assignee.name} className="h-full w-full object-cover" />
                              ) : (
                                <span className="text-xs font-semibold">
                                  {assignee.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                                </span>
                              )}
                            </div>
                            <div>
                              <div className="text-sm font-medium">{assignee.name}</div>
                              {!assignee.inChatbotGG && (
                                <div className="text-xs text-orange-500">External user</div>
                              )}
                            </div>
                          </div>
                        ))}
                        {selectedTask.assignees.length === 0 && (
                          <div className="text-gray-400">No assignees</div>
                        )}
                      </div>
                    </div>

                    <div>
                      <h4 className="text-sm font-medium text-gray-400 mb-2">Tags</h4>
                      <div className="flex flex-wrap gap-1">
                        {selectedTask.tags.map(tag => (
                          <span key={tag} className="px-2.5 py-1 rounded-full text-xs bg-gray-700 text-gray-300">
                            {tag}
                          </span>
                        ))}
                        {selectedTask.tags.length === 0 && (
                          <div className="text-gray-400">No tags</div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Blocker Detection Popup */}
      <AnimatePresence>
        {blockerDetection.isOpen && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-primary rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto custom-scrollbar border border-border-color"
            >
              <div className="sticky top-0 z-10 bg-primary p-6 border-b border-border-color flex justify-between items-center backdrop-blur-md bg-opacity-95">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <div className="bg-gradient-to-r from-orange-500 to-red-500 p-2 rounded-lg">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                    </svg>
                  </div>
                  Blocker Detection Report
                </h2>
                <div className="flex gap-2">
                  <button
                    onClick={handleCloseBlockerDetection}
                    className="p-2 rounded-lg hover:bg-tertiary transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="p-6">
                <iframe
                  srcDoc={generateBlockerReport()}
                  className="w-full h-[70vh] border-0"
                  title="Blocker Detection Report"
                />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Add a floating reasons modal */}
      <AnimatePresence>
        {selectedReason && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedReason(null)}
          >
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm"></div>
            <motion.div
              className="relative bg-secondary border border-border-color rounded-xl shadow-2xl max-w-md w-full overflow-hidden"
              onClick={(e) => e.stopPropagation()}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 500, damping: 30 }}
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-indigo-600/20 via-purple-500/10 to-pink-500/5 rounded-full transform translate-x-16 -translate-y-16 blur-xl"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-indigo-600/20 via-purple-500/10 to-pink-500/5 rounded-full transform -translate-x-12 translate-y-12 blur-xl"></div>

              <div className="p-4 border-b border-border-color bg-gradient-to-r from-indigo-600/20 to-purple-600/20 flex justify-between items-center relative">
                <div className="flex items-center gap-2">
                  <div className="bg-indigo-500/20 p-2 rounded-lg">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-indigo-400">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09Z" />
                    </svg>
                  </div>
                  <h3 className="font-semibold">AI Priority Reasoning</h3>
                </div>
                <button
                  onClick={() => setSelectedReason(null)}
                  className="text-gray-400 hover:text-white p-1.5 hover:bg-gray-800/50 rounded-lg transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-5 relative">
                {/* Task name */}
                <div className="mb-3 font-medium text-lg">
                  {tasks.find(t => t.id === selectedReason.taskId)?.name}
                </div>

                <div className="flex items-center gap-3 mb-4">
                  <div className={`px-3 py-1 rounded-full text-xs font-medium ${priorityColors[selectedReason.oldPriority]}`}>
                    {selectedReason.oldPriority}
                  </div>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-gray-400">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                  </svg>
                  <div className={`px-3 py-1 rounded-full text-xs font-medium ${priorityColors[selectedReason.newPriority]}`}>
                    {selectedReason.newPriority}
                  </div>
                </div>

                <div className="bg-tertiary rounded-lg p-4 border border-border-color relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/5 via-transparent to-transparent"></div>
                  <p className="text-gray-200 relative">
                    {selectedReason.reason}
                  </p>
                </div>

                <div className="mt-5 flex justify-end">
                  <button
                    onClick={() => setSelectedReason(null)}
                    className="flex items-center gap-2 px-4 py-2 bg-accent hover:bg-accent/90 rounded-lg transition-colors text-white text-sm font-medium shadow-lg shadow-accent/20"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                    </svg>
                    Got it
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Dummy data for demonstration
const dummyTeamMembers = [
  {
    id: '1',
    name: 'Alex Smith',
    role: 'Frontend Developer',
    email: 'alex@example.com',
    photo: null
  },
  {
    id: '2',
    name: 'Taylor Johnson',
    role: 'Product Manager',
    email: 'taylor@example.com',
    photo: null
  },
  {
    id: '3',
    name: 'Jordan Williams',
    role: 'UI/UX Designer',
    email: 'jordan@example.com',
    photo: null
  },
  {
    id: '4',
    name: 'Casey Brown',
    role: 'Backend Developer',
    email: 'casey@example.com',
    photo: null
  }
];

const dummyTasks: Task[] = [
  {
    id: 'task-1',
    name: 'Implement responsive design',
    description: 'Make the chat interface responsive for mobile devices.',
    dueDate: new Date(new Date().setDate(new Date().getDate() + 2)).toISOString(), // 2 days from now
    assignees: [
      { id: '1', name: 'Alex Smith', inChatbotGG: true, avatar: null },
      { id: '3', name: 'Jordan Williams', inChatbotGG: true, avatar: null }
    ],
    priority: 'high',
    status: 'in progress',
    tags: ['design', 'frontend', 'mobile'],
    subtasks: []
  },
  {
    id: 'task-2',
    name: 'Add authentication system',
    description: 'Implement user authentication with JWT tokens.',
    dueDate: new Date(new Date().setDate(new Date().getDate() + 1)).toISOString(), // 1 day from now
    assignees: [
      { id: '4', name: 'Casey Brown', inChatbotGG: true, avatar: null },
      { id: 'ext-1', name: 'Morgan Lee', inChatbotGG: false, avatar: null }
    ],
    priority: 'medium', // This will likely be changed to urgent by AI
    status: 'to do',
    tags: ['security', 'backend'],
    subtasks: []
  },
  {
    id: 'task-3',
    name: 'Design product dashboard',
    description: 'Create wireframes and prototypes for the main dashboard.',
    dueDate: new Date(new Date().setDate(new Date().getDate() + 7)).toISOString(), // 7 days from now
    assignees: [
      { id: '3', name: 'Jordan Williams', inChatbotGG: true, avatar: null }
    ],
    priority: 'medium',
    status: 'done',
    tags: ['design', 'dashboard'],
    subtasks: []
  },
  {
    id: 'task-4',
    name: 'Sprint planning',
    description: 'Prepare user stories and tasks for the next sprint.',
    dueDate: new Date(new Date().setDate(new Date().getDate())).toISOString(), // Today
    assignees: [
      { id: '2', name: 'Taylor Johnson', inChatbotGG: true, avatar: null },
      { id: 'ext-2', name: 'Jamie Wilson', inChatbotGG: false, avatar: null }
    ],
    priority: 'medium',
    status: 'blocked',
    tags: ['planning', 'project management'],
    subtasks: []
  },
  {
    id: 'task-5',
    name: 'Code review for PR #123',
    description: 'Review pull request #123 for new chat features.',
    dueDate: new Date(new Date().setDate(new Date().getDate() + 5)).toISOString(), // 5 days from now
    assignees: [
      { id: '1', name: 'Alex Smith', inChatbotGG: true, avatar: null },
      { id: '4', name: 'Casey Brown', inChatbotGG: true, avatar: null }
    ],
    priority: 'low',
    status: 'review',
    tags: ['code review', 'chat'],
    subtasks: []
  }
];

export default Tasks;
